import { getContentstackImagesForWidths } from '@noths/polaris-client-utils';
import dynamic from 'next/dynamic';

import type { HeroBannerSectionProps } from 'src/components/sections/HeroBannerSection/HeroBannerSection';
import { HeroBannerContent } from 'src/featureFlags/featureFlags';
import type { SectionRenderer } from 'src/types/Section';
import type { Transformer } from 'src/types/Transformer';
import { lazyLoadImages } from 'src/utils/images';
import type { HeroBannerSectionContent } from './types';

/* istanbul ignore next */
export const renderer: SectionRenderer<HeroBannerSectionProps> = dynamic<HeroBannerSectionProps>(
  () =>
    import('src/components/sections/HeroBannerSection/HeroBannerSection').then(
      (mod) => mod.HeroBannerSection,
    ),
);

const getVariantPropertyName = (propertyName: string, featureFlagValue: string | undefined) => {
  switch (featureFlagValue) {
    case HeroBannerContent.HeroA:
      return `${propertyName}_promo1`;
    case HeroBannerContent.HeroB:
      return `${propertyName}_promo2`;
    default:
      return propertyName;
  }
};

/* istanbul ignore next */
export const transformer: Transformer<HeroBannerSectionContent, HeroBannerSectionProps> = (
  content,
  { pageConfig, pageMetadata, positionMetadata },
) => {
  const heroBannerContentFeatureFlag = pageMetadata.featureFlags?.[HeroBannerContent.NAME];

  return {
    uid: content._metadata.uid,
    props: {
      backgroundColor: content.background_color,
      headingIsPageTitle: positionMetadata.position === 0 && pageConfig.pageType !== 'homepage',
      images: getContentstackImagesForWidths(
        content.image.image_source.url,
        [360, 500, 700, 900, 1132],
        {
          aspectRatio: '16:9',
        },
      ),
      imageWidth: 360,
      imageHeight: 216,
      imageAlt: content.image.alt_text,
      textColorMode: content.text_color_mode,
      headingLine1: content.heading[getVariantPropertyName('line1', heroBannerContentFeatureFlag)],
      headingLine2: content.heading[getVariantPropertyName('line2', heroBannerContentFeatureFlag)],
      ...(content.heading.underline.show_underline && {
        headingUnderlineColor: content.heading.underline.underline_color,
      }),
      subHeading:
        content[getVariantPropertyName('sub_heading', heroBannerContentFeatureFlag)] || '',
      copy: content[getVariantPropertyName('copy', heroBannerContentFeatureFlag)] || '',
      linkHref: content.button?.[getVariantPropertyName('url', heroBannerContentFeatureFlag)] || '',
      buttonText:
        content.button?.[getVariantPropertyName('text', heroBannerContentFeatureFlag)] || '',
      buttonTextColorMode: content.button?.text_color || 'light',
      trackingTitle:
        content[getVariantPropertyName('tracking_title', heroBannerContentFeatureFlag)],
      lazyImages: lazyLoadImages(positionMetadata.position),
    },
  };
};
